import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { genres, getMoviesByGenre } from '@/mocks/movies';

export default function CategoryPage() {
  const { genre = 'all' } = useParams<{ genre: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'default'>('default');

  const currentGenre = genres.find((g) => g.key === genre) || genres[0];
  const filteredMovies = useMemo(() => getMoviesByGenre(genre), [genre]);

  const sortedMovies = useMemo(() => {
    const list = [...filteredMovies];
    if (sortBy === 'rating') {
      return list.sort((a, b) => b.rating - a.rating);
    }
    if (sortBy === 'year') {
      return list.sort((a, b) => b.year - a.year);
    }
    return list;
  }, [filteredMovies, sortBy]);

  const genreLabel = currentGenre.label;
  const pageTitle = genre === 'all'
    ? '全部电影分类 | 星空影院在线观看免费'
    : `${genreLabel}电影 | 星空影院在线观看免费`;
  const pageDesc = genre === 'all'
    ? '星空影院提供海量高清电影分类浏览，包括动作、喜剧、剧情、科幻、恐怖、爱情、动画、纪录片等多种类型，满足您的观影需求。'
    : `星空影院${genreLabel}电影分类，精选优质${genreLabel}影片在线观看免费，画质高清流畅，即刻畅享精彩${genreLabel}内容。`;
  const pageKeywords = genre === 'all'
    ? '星空影院电影分类,电影类型,免费在线观看电影,高清电影免费播放,星空影院在线观看免费'
    : `星空影院${genreLabel}电影,${genreLabel}片免费看,免费${genreLabel}电影在线观看,高清${genreLabel}电影`;

  const schemaOrg = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDesc,
    url: `https://yukafile.github.io/category/${genre}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '星空影院',
      url: 'https://yukafile.github.io',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://yukafile.github.io/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: genre === 'all' ? '全部电影' : `${genreLabel}电影`,
        item: `https://yukafile.github.io/category/${genre}`,
      },
    ],
  };

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={pageKeywords} />
      <link rel="canonical" href={`https://yukafile.github.io/category/${genre}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`https://yukafile.github.io/category/${genre}`} />
      <script type="application/ld+json">
        {JSON.stringify(schemaOrg)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>

      <div className="pt-20 md:pt-24 pb-10 md:pb-14 px-4 md:px-6">
        <div className="max-w-[1280px] mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: genre === 'all' ? '全部电影' : genreLabel },
            ]}
          />

          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mt-4 mb-2">
            {genre === 'all' ? '全部影片' : `${genreLabel}电影`}
          </h1>
          <p className="text-sm text-foreground-500 mb-6 md:mb-8">{pageDesc}</p>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6 md:mb-8">
            <div className="flex items-center gap-2 flex-wrap">
              {genres.map((g) => (
                <Link
                  key={g.key}
                  to={`/category/${g.key}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    g.key === genre
                      ? 'bg-primary-500 text-background-950'
                      : 'bg-background-800/60 text-foreground-400 hover:bg-background-700/60 hover:text-foreground-200 border border-background-700/50'
                  }`}
                >
                  {g.label}
                </Link>
              ))}
            </div>

            <div className="sm:ml-auto flex items-center gap-2">
              <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
              <button
                onClick={() => setSortBy('default')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  sortBy === 'default'
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-800/60 text-foreground-500 hover:text-foreground-300'
                }`}
              >
                默认
              </button>
              <button
                onClick={() => setSortBy('rating')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  sortBy === 'rating'
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-800/60 text-foreground-500 hover:text-foreground-300'
                }`}
              >
                评分最高
              </button>
              <button
                onClick={() => setSortBy('year')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  sortBy === 'year'
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-800/60 text-foreground-500 hover:text-foreground-300'
                }`}
              >
                最新上映
              </button>
            </div>
          </div>

          {sortedMovies.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {sortedMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-800/60">
                <i className="ri-movie-line text-foreground-500 text-2xl"></i>
              </div>
              <p className="text-foreground-500 text-lg">暂无该分类的影片</p>
              <Link
                to="/category/all"
                className="inline-block mt-4 px-5 py-2.5 bg-primary-500 text-background-950 rounded-full text-sm font-medium hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                查看全部影片
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
}